"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { HighlightedText } from "@shared/ui";
import { PinLocationIcon } from "@shared/ui/custom-icon";
import { getSlug, imageFallback, transformQueryParamsWithoutLocale } from "@shared/utils";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";

type Government = {
  id: string;
  name: string;
  logoUri: string;
  cityName: string;
  employerId: string;
};

export function GovernmentResult({ keyword, onClick }: { keyword: string; onClick: () => void }) {
  const restDataProviderClient = createRestDataProviderClient();

  const { data: result, isFetching } = useQuery<{ data: Government[] }>({
    queryKey: ["header-search-governments", keyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/onboarding/v2/governments/verified", {
          limit: 3,
          keyword: keyword,
        }),
        method: "GET",
      }),
    enabled: true,
  });

  if (isFetching) {
    return (
      <div>
        <h4 className="p-3 font-bold text-lg">Instansi Pemerintah</h4>
        <div className="space-y-2 mt-1">
          {[1, 2, 3].map((it) => {
            return (
              <div className="rounded-lg relative h-10 overflow-hidden" key={it}>
                <Skeleton className="absolute w-full h-full bg-gray-200" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (result && result.data?.length <= 0) {
    return <></>;
  }

  return (
    <div>
      <h4 className="p-3 font-bold text-lg">Instansi Pemerintah</h4>
      <div>
        {result?.data?.map((it) => {
          return (
            <Link
              key={it.id}
              href={`/instansi-pemerintah/${getSlug(it.id, it.name)}`}
              className="flex items-start hover:bg-gray-200 rounded-lg p-3 gap-3"
              onClick={onClick}
            >
              <Image
                src={it.logoUri || "/blank-white.webp"}
                className="w-12 h-12 min-w-12 rounded-lg bg-gray-100 object-cover border border-gray-200 mt-0.5"
                width="48"
                height="48"
                alt={it.name}
                onError={imageFallback()}
              />
              <div className="flex-1">
                <div className="font-semibold text-base">
                  <HighlightedText keyword={keyword} text={it.name} />
                </div>
                {it.cityName && (
                  <div className="mt-2 text-xs flex items-start gap-1">
                    <PinLocationIcon className="size-3 min-w-3 mt-0.5" />
                    <span className="flex-1">{it.cityName}</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
